// TODO: Replace localStorage with API calls (SSoT: backend is source of truth)

import * as React from "react";

export interface FavoriteItem {
	id: string;
	href: string;
	title: string;
	icon?: string;
	folder?: string;
	order: number;
	addedAt: number;
}

export interface RecentItem {
	href: string;
	title: string;
	visitedAt: number;
}

interface FavoritesContextProps {
	favorites: FavoriteItem[];
	recentPages: RecentItem[];
	addFavorite: (item: Omit<FavoriteItem, "id" | "order" | "addedAt">) => void;
	removeFavorite: (href: string) => void;
	isFavorite: (href: string) => boolean;
	reorderFavorites: (fromIndex: number, toIndex: number) => void;
	addToRecent: (href: string, title: string) => void;
	getFavoriteByHref: (href: string) => FavoriteItem | undefined;
}

const FavoritesContext = React.createContext<FavoritesContextProps | null>(null);

const FAVORITES_KEY = "farmaster_favorites";
const RECENT_KEY = "farmaster_recent";
const MAX_RECENT = 8;

function readFromStorage<T>(key: string, fallback: T): T {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return fallback;
		return JSON.parse(raw) as T;
	} catch (error) {
		console.error(
			`Failed to read "${key}" from localStorage: ${error instanceof Error ? error.message : String(error)}`,
		);
		return fallback;
	}
}

function writeToStorage(key: string, value: unknown): void {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.error(
			`Failed to write "${key}" to localStorage: ${error instanceof Error ? error.message : String(error)}`,
		);
	}
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
	const [favorites, setFavorites] = React.useState<FavoriteItem[]>([]);
	const [recentPages, setRecentPages] = React.useState<RecentItem[]>([]);
	const [isLoaded, setIsLoaded] = React.useState(false);

	React.useEffect(() => {
		setFavorites(readFromStorage<FavoriteItem[]>(FAVORITES_KEY, []));
		setRecentPages(readFromStorage<RecentItem[]>(RECENT_KEY, []));
		setIsLoaded(true);
	}, []);

	React.useEffect(() => {
		if (isLoaded) {
			writeToStorage(FAVORITES_KEY, favorites);
		}
	}, [favorites, isLoaded]);

	React.useEffect(() => {
		if (isLoaded) {
			writeToStorage(RECENT_KEY, recentPages);
		}
	}, [recentPages, isLoaded]);

	const addFavorite = React.useCallback((item: Omit<FavoriteItem, "id" | "order" | "addedAt">) => {
		setFavorites((prev) => {
			if (prev.some((f) => f.href === item.href)) {
				return prev;
			}
			const newItem: FavoriteItem = {
				...item,
				id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36),
				order: prev.length,
				addedAt: Date.now(),
			};
			return [...prev, newItem];
		});
	}, []);

	const removeFavorite = React.useCallback((href: string) => {
		setFavorites((prev) => {
			const filtered = prev.filter((f) => f.href !== href);
			return filtered.map((item, index) => ({ ...item, order: index }));
		});
	}, []);

	const isFavorite = React.useCallback(
		(href: string) => {
			return favorites.some((f) => f.href === href);
		},
		[favorites],
	);

	const getFavoriteByHref = React.useCallback(
		(href: string) => {
			return favorites.find((f) => f.href === href);
		},
		[favorites],
	);

	const reorderFavorites = React.useCallback((fromIndex: number, toIndex: number) => {
		setFavorites((prev) => {
			const result = Array.from(prev);
			const [removed] = result.splice(fromIndex, 1);
			result.splice(toIndex, 0, removed);
			return result.map((item, index) => ({ ...item, order: index }));
		});
	}, []);

	const addToRecent = React.useCallback((href: string, title: string) => {
		setRecentPages((prev) => {
			const filtered = prev.filter((r) => r.href !== href);
			const newRecent: RecentItem = {
				href,
				title,
				visitedAt: Date.now(),
			};
			return [newRecent, ...filtered].slice(0, MAX_RECENT);
		});
	}, []);

	return (
		<FavoritesContext.Provider
			value={{
				favorites,
				recentPages,
				addFavorite,
				removeFavorite,
				isFavorite,
				reorderFavorites,
				addToRecent,
				getFavoriteByHref,
			}}
		>
			{children}
		</FavoritesContext.Provider>
	);
}

export function useFavorites() {
	const context = React.useContext(FavoritesContext);
	if (!context) {
		throw new Error("useFavorites must be used within FavoritesProvider");
	}
	return context;
}
